import React, { useEffect, useRef, useState } from "react";
import { CheckIcon, MonitorIcon, MoonIcon, SunIcon } from "lucide-react";
import { useTheme } from "../theme";

const OPTIONS = [
    { value: "light", label: "Light", icon: SunIcon },
    { value: "dark", label: "Dark", icon: MoonIcon },
    { value: "system", label: "System", icon: MonitorIcon },
];

/**
 * Quick theme picker for signed-out visitors, who have no profile menu to reach Settings from.
 * Signed-in users change the theme on the Settings page instead.
 */
export default function ThemeMenu() {
    const { theme, resolvedTheme, setTheme } = useTheme();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onPointer = (event) => {
            if (ref.current && !ref.current.contains(event.target)) setOpen(false);
        };
        const onKey = (event) => {
            if (event.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onPointer);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onPointer);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const CurrentIcon = resolvedTheme === "dark" ? MoonIcon : SunIcon;

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((value) => !value)}
                aria-label="Change theme"
                aria-haspopup="menu"
                aria-expanded={open}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-graphite shadow-[0_0_0_1px_var(--hairline)] transition-colors hover:bg-hover-tint hover:text-secondary"
            >
                <CurrentIcon className="h-4 w-4" aria-hidden="true" />
            </button>

            {open ? (
                <div
                    role="menu"
                    aria-label="Theme"
                    className="absolute right-0 z-20 mt-2 w-40 origin-top-right rounded-2xl bg-white p-2 shadow-[0_0_0_1px_var(--hairline)]"
                >
                    {OPTIONS.map(({ value, label, icon: Icon }) => (
                        <button
                            key={value}
                            type="button"
                            role="menuitemradio"
                            aria-checked={theme === value}
                            onClick={() => {
                                setTheme(value);
                                setOpen(false);
                            }}
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left font-Mulish text-sm text-secondary hover:bg-primary-50"
                        >
                            <Icon className="h-4 w-4" aria-hidden="true" />
                            <span className="flex-1">{label}</span>
                            {theme === value ? <CheckIcon className="h-4 w-4 text-primary-700" aria-hidden="true" /> : null}
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    );
}
